<template>
    <div class="class-1">
        <!-- Header -->
        <AppHeader title="Floors"/>

        <!-- Floors Section -->
        <div class="mt-4">
            <div class="row">
                <div class="col-12">
                    <div class="text-right mb-3">
                        <button class="add-floor-btn" @click="showAddFloorModal = true">Create New Floor</button>
                    </div>
                    
                    <!-- Branch Filter Dropdown -->
                    <div class="filter-section mb-3" style="margin-left: 1rem;">
                        <label for="branchFilter">Filter by Branch:</label>
                        <select id="branchFilter" v-model="selectedBranch" @change="filterByBranch" class="form-control">
                            <option value="">All Branches</option>
                            <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                                {{ branch.name }}
                            </option>
                        </select>
                    </div>

                    <div v-if="loading" class="text-center">Loading floors...</div>
                    <div v-else>
                        <!-- Floors Table -->
                        <table>
                            <thead>
                                <tr>
                                    <th>Floor Name</th>
                                    <th>Branch</th>
                                    <th>Created At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="floor in filteredFloors" :key="floor.id" class="action-buttons">
                                    <td>{{ floor.floor }}</td>
                                    <td>{{ getBranchName(floor.branchId) }}</td>
                                    <td>{{ formatDate(floor.created_at) }}</td>
                                    <td>
                                        <button data-tooltip="Update" @click="editFloor(floor)">
                                            <img src="@/assets/edit_icon.png" alt="Update">
                                        </button>
                                        <button data-tooltip="Delete" @click="deleteFloor(floor)">
                                            <img src="@/assets/delete_icon.png" alt="Delete">
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="filteredFloors.length === 0">
                                    <td colspan="4" class="text-center">No floors found</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Floor Modal -->
        <div v-if="showAddFloorModal" class="modal-overlay" @click="showAddFloorModal = false">
            <div class="modal-container" @click.stop>
                <div class="modal-header">
                    <h3>Create New Floor</h3>
                    <button class="close-btn" @click="showAddFloorModal = false">&times;</button>
                </div>
                <div class="modal-body">
                    <!-- Create Floor Form -->
                    <form @submit.prevent="handleCreateFloor">
                        <!-- Floor Name Input -->
                        <div class="form-row">
                            <div class="form-group col-md-12">
                                <label for="modal-floor">Floor Name <span class="required">*</span></label>
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    id="modal-floor" 
                                    v-model="floorForm.floor" 
                                    placeholder="Enter Floor Name (e.g., Ground Floor, First Floor)" 
                                    required
                                    maxlength="50"
                                >
                                <small class="">Maximum 50 characters</small>
                            </div>
                        </div>

                        <!-- Branch Selection -->
                        <div class="form-row">
                            <div class="form-group col-md-12">
                                <label for="modal-branchId">Select Branch</label>
                                <select 
                                    class="form-control" 
                                    id="modal-branchId" 
                                    v-model="floorForm.branchId"
                                >
                                    <option value="">-- Select Branch (Optional) --</option>
                                    <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <small class="">Select a branch to associate this floor with</small>
                            </div>
                        </div>

                        <!-- Buttons -->
                        <div class="form-row">
                            <div class="form-group col-12 d-flex justify-content-end gap-2">
                                <button type="button" class="btn btn-secondary" @click="showAddFloorModal = false">
                                    Cancel
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="submitting">
                                    {{ submitting ? 'Creating...' : 'Create Floor' }}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            floors: [],
            branches: [],
            loading: true,
            selectedBranch: '',
            filteredFloors: [],
            currentDate: '',
            currentTime: '',
            showAddFloorModal: false,
            submitting: false,
            floorForm: {
                floor: '',
                branchId: '',
            },
        };
    },
    mounted() {
        this.updateDateTime();
        this.startDateTimeUpdate();
        this.fetchFloors();
        this.fetchBranches();
    },
    methods: {
        navigateToTables(floor) {
            this.$router.push({
                name: "Tables",
                params: { floorId: floor.id }
            });
        },
        
        async fetchFloors() {
            try {
                this.$root.isLoading = true;
                this.loading = true;
                const token = localStorage.getItem('authToken');
                const appId = localStorage.getItem('appId') || 1;
                
                const response = await fetch(`${this.$BASE_API_URL}/api/floors?appId=${appId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch floors');
                }

                const data = await response.json();
                this.floors = data.data;
                this.filteredFloors = this.floors;
            } catch (error) {
                console.error('Error fetching floors:', error);
                this.$toast.error('Failed to load floors. Please try again.');
            } finally {
                this.$root.isLoading = false;
                this.loading = false;
            }
        },

        async fetchBranches() {
            try {
                const token = localStorage.getItem('authToken');
                const appId = localStorage.getItem('appId') || 1;
                
                const response = await fetch(`${this.$BASE_API_URL}/api/branches?appId=${appId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch branches');
                }

                const data = await response.json();
                this.branches = data.data;
            } catch (error) {
                console.error('Error fetching branches:', error);
            }
        },

        filterByBranch() {
            if (!this.selectedBranch) {
                this.filteredFloors = this.floors;
            } else {
                this.filteredFloors = this.floors.filter(floor => floor.branchId == this.selectedBranch);
            }
        },

        getBranchName(branchId) {
            if (!branchId) return 'No Branch';
            const branch = this.branches.find(b => b.id === branchId);
            return branch ? branch.name : 'Unknown Branch';
        },

        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },

        createNewFloor() {
            this.showAddFloorModal = true;
        },

        async handleCreateFloor() {
            if (!this.floorForm.floor.trim()) {
                this.$toast.error('Floor name is required');
                return;
            }

            this.submitting = true;
            
            try {
                this.$root.isLoading = true;
                const token = localStorage.getItem('authToken');
                const appId = localStorage.getItem('appId') || 1;

                const payload = {
                    appId: parseInt(appId),
                    floor: this.floorForm.floor.trim(),
                };

                // Only include branchId if it's selected
                if (this.floorForm.branchId) {
                    payload.branchId = parseInt(this.floorForm.branchId);
                }

                const response = await fetch(`${this.$BASE_API_URL}/api/floors`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });

                const data = await response.json();

                if (response.ok) {
                    this.$toast.success('Floor created successfully!');
                    this.showAddFloorModal = false;
                    this.floorForm.floor = '';
                    this.floorForm.branchId = '';
                    this.fetchFloors(); // Refresh the list
                } else {
                    this.$toast.error(data.message || 'Failed to create floor');
                    
                    // Handle validation errors
                    if (data.errors) {
                        Object.values(data.errors).forEach(error => {
                            this.$toast.error(error[0]);
                        });
                    }
                }
            } catch (error) {
                console.error('Error:', error);
                this.$toast.error('An error occurred. Please try again.');
            } finally {
                this.$root.isLoading = false;
                this.submitting = false;
            }
        },

        editFloor(floor) {
            console.log(floor);
           this.$router.push({ 
                    name: "EditFloorPage", 
                    params: { id: floor.id }
                });
        },

        async deleteFloor(floor) {
            if (confirm(`Are you sure you want to delete floor "${floor.floor}"?`)) {
                try {
                    const token = localStorage.getItem('authToken');
                    const appId = localStorage.getItem('appId') || 1;
                    
                    const response = await fetch(`${this.$BASE_API_URL}/api/floors/${floor.id}?appId=${appId}`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (response.status === 204) {
                        this.$toast.success('Floor deleted successfully!');
                        this.fetchFloors();
                    } else {
                        const data = await response.json();
                        
                        // Handle the error structure from your API
                        if (data.error && data.error.message) {
                            const errorMsg = data.error.message;
                            
                            if (errorMsg.includes('has associated tables')) {
                                // Show a more user-friendly message
                                this.$toast.error(`❌ Cannot delete floor "${floor.floor}" because it contains tables.\n\nPlease delete all tables in this floor first before deleting the floor.`, {
                                    duration: 6000,
                                    closeable: true,
                                    position: 'top-center'
                                });
                                
                                // Optional: Add a button to navigate to tables view
                                if (confirm('Would you like to view the tables in this floor?')) {
                                    this.$router.push({
                                        name: "Tables",
                                        params: { floorId: floor.id }
                                    });
                                }
                            } else {
                                this.$toast.error(errorMsg);
                            }
                        } else {
                            this.$toast.error(data.message || 'Failed to delete floor');
                        }
                    }
                } catch (error) {
                    console.error('Error deleting floor:', error);
                    this.$toast.error('An unexpected error occurred. Please try again.');
                }
            }
        },

        updateDateTime() {
            const now = new Date();
            this.currentDate = now.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
            this.currentTime = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
        },

        startDateTimeUpdate() {
            setInterval(() => {
                this.updateDateTime();
            }, 1000);
        },
    },
    watch: {
        floors: {
            handler() {
                this.filterByBranch();
            },
            deep: true
        }
    }
};
</script>

<style scoped>
.container {
    max-width: 800px;
    margin: 0 auto;
}

.filter-section {
    max-width: 300px;
    margin-bottom: 1rem;
}

.filter-section label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.action-buttons button {
    background: none;
    border: none;
    cursor: pointer;
    margin: 0 5px;
    position: relative;
    transition: transform 0.2s ease, opacity 0.2s ease;
}

.action-buttons button:hover {
    transform: scale(1.2);
    opacity: 0.8;
}

.action-buttons button:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: -25px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    padding: 5px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
}

.action-buttons button img {
    width: 20px;
    height: 20px;
}

/* Icon Colors */
.bi.bi-grid-3x3-gap-fill {
    filter: invert(50%) sepia(100%) saturate(500%) hue-rotate(180deg); /* Blue */
    font-size: 1.2rem;
}

.action-buttons button[data-tooltip="Update"] img {
    filter: invert(50%) sepia(100%) saturate(500%) hue-rotate(90deg); /* Green */
}

.add-floor-btn {
    padding: 10px;
    background-color: #4caf50;
    border: none;
    cursor: pointer;
    width: max-content;
    border-radius: 5px;
    margin-right: 1rem;
    color: white;
    font-weight: 500;
}

.add-floor-btn:hover {
    background-color: #45a049;
}

.form-control {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

th {
    font-weight: 600;
}

.text-center {
    text-align: center;
}

.mb-3 {
    margin-bottom: 1rem;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-container {
    background-color: var(--bg-color);
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6c757d;
}

.close-btn:hover {
    color: #343a40;
}

.modal-body {
    padding: 1.5rem;
}

.gap-2 {
    gap: 0.5rem;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
}

.btn-primary {
    background-color: #4caf50;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #45a049;
}

.btn-primary:disabled {
    background-color: #a5d6a7;
    cursor: not-allowed;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: #5a6268;
}

.required {
    color: red;
}
</style>